/// <amd-module name="Repos/Views/Branches/Components/BranchesTable/BranchesTable" />
import * as React from "react";
import * as Resources from "Repos/Views/Branches/Resources";
import { IObservableLikeValue, ObservableLike } from "VSS/Core/Observable";
import { VssComponent } from "VSS/Platform/Layout";
import { Card } from "VSSUI/Card";
import { ColumnMore, ITableColumn, ITableRowDetails, Table, TableLoadingRow } from "VSSUI/Table";
import { EnhancedGitRef } from "../../Data/EnhancedGitRef";
import * as BranchColumns from "../Columns/BranchColumns";
import { IBranchesTableProps } from "./BranchesTable.Props";

export class BranchesTable extends VssComponent<IBranchesTableProps> {
    private columnCache: ITableColumn<IObservableLikeValue<EnhancedGitRef> | BranchColumns.UniversalBranchColumnContent>[];
    private readonly tableRef = React.createRef<Table<EnhancedGitRef>>();

    public render() {
        const { itemProvider, onRowActivate } = this.props;
        const columns = this.getColumns();

        return (
            <Card className="bolt-table-card bolt-card-white" contentProps={{ contentPadding: false }}>
                <Table<IObservableLikeValue<EnhancedGitRef>>
                    ariaLabel={Resources.BranchesTable}
                    columns={columns}
                    itemProvider={itemProvider.baseProvider}
                    onActivate={(event, row) => onRowActivate(ObservableLike.getValue(row.data))}
                    ref={this.tableRef}
                    renderLoadingRow={(
                        rowIndex: number,
                        rowDetails: ITableRowDetails<BranchColumns.UniversalBranchColumnContent | IObservableLikeValue<EnhancedGitRef | undefined>>
                    ) => (
                        <AsyncLoadingRow
                            columns={columns}
                            rowIndex={rowIndex}
                            rowDetails={rowDetails}
                            onMount={() => itemProvider.isInitialized() && itemProvider.loadNextPage()}
                        />
                    )}
                    selectableText={true}
                    tableBreakpoints={BranchColumns.StandardBreakpoints}
                />
            </Card>
        );
    }

    private getColumns() {
        if (!this.columnCache) {
            const {
                addContributedMenuItems,
                isUserMember,
                itemProvider,
                onCheckPermission,
                onCreate,
                onDelete,
                onLock,
                onSetAsCompare,
                onSetAsDefault,
                onSetFavorite,
                onShowSecurityDialog,
                onUnlock
            } = this.props;

            this.columnCache = [
                BranchColumns.NameColumn(itemProvider.pathHasPolicy), // Don't need the routing service because it's only used for folders
                BranchColumns.CommitColumn,
                BranchColumns.AuthorColumn,
                BranchColumns.AuthoredDateColumn,
                BranchColumns.BehindAheadColumn(itemProvider.getCompareBranch()),
                BranchColumns.StatusColumn,
                BranchColumns.PullRequestColumn,
                BranchColumns.FavoriteColumn(onSetFavorite),
                BranchColumns.ActionsColumn(
                    addContributedMenuItems,
                    isUserMember,
                    onCheckPermission,
                    onCreate,
                    onDelete,
                    onLock,
                    onUnlock,
                    onSetAsCompare,
                    onSetAsDefault,
                    onShowSecurityDialog,
                    undefined,
                    this.tableRef
                )
            ];
        }

        return this.columnCache;
    }
}

interface AsyncLoadingRowProps {
    columns: (BranchColumns.UniversalBranchColumn | ColumnMore<BranchColumns.UniversalBranchColumnContent>)[];
    onMount: () => void;
    rowDetails: ITableRowDetails<BranchColumns.UniversalBranchColumnContent | IObservableLikeValue<EnhancedGitRef | undefined>>;
    rowIndex: number;
}

const AsyncLoadingRow: React.FunctionComponent<AsyncLoadingRowProps> = props => {
    const { columns, onMount, rowDetails, rowIndex } = props;

    React.useEffect(() => {
        onMount();
    }, []);

    return <TableLoadingRow columns={columns} details={rowDetails} rowIndex={rowIndex} />;
};
